/**
 * Remembering *which* file an interrupted upload was, so resuming it costs no clicks.
 *
 * R2 is the source of truth for what has actually been uploaded — none of this is load-bearing.
 * All it saves is the file picker: on Chromium a `FileSystemFileHandle` survives a reload in
 * IndexedDB, so Resume can re-open the same file directly. Firefox and Safari have no handle to
 * store, so every function here quietly no-ops and the caller falls back to asking the user to
 * pick the file again. Resume still works there; it just costs one extra click.
 *
 * Everything is wrapped in try/catch on purpose: IndexedDB throws outright in a private window and
 * in browsers set to block site data, and losing a convenience must never fail an upload.
 */

const DB_NAME = 'censorme-uploads';
const DB_VERSION = 1;
const STORE = 'fileHandles';

/**
 * Not in TypeScript's DOM lib yet, and only Chromium implements it. Declared narrowly — just the
 * two calls used here — rather than pulling in a types package for a nice-to-have.
 */
interface StoredFileHandle {
  getFile: () => Promise<File>;
  queryPermission?: (descriptor: {mode: 'read' | 'readwrite'}) => Promise<PermissionState>;
  requestPermission?: (descriptor: {mode: 'read' | 'readwrite'}) => Promise<PermissionState>;
}

interface FilePickerWindow {
  showOpenFilePicker?: (options: {
    multiple?: boolean;
    types?: {description: string; accept: Record<string, string[]>}[];
  }) => Promise<StoredFileHandle[]>;
}

interface HandleCapableDataTransferItem extends DataTransferItem {
  getAsFileSystemHandle?: () => Promise<StoredFileHandle | null>;
}

/**
 * `showOpenFilePicker`'s accept map, which is MIME type → extensions and takes no wildcards — so it
 * cannot simply reuse `AUDIO_ACCEPT`, which is an `<input accept>` string. The list mirrors the
 * formats that input already offers; the extension list is what the picker actually filters on.
 */
export const SHOW_PICKER_AUDIO_TYPES: Record<string, string[]> = {
  'audio/*': [
    '.aif', '.aiff', '.ape', '.flac', '.ogg', '.aac', '.mp2', '.mp3', '.wav', '.wma', '.m4a',
  ],
};

/** A file the user chose, plus the handle to re-open it later when the browser offers one. */
export interface PickedAudioFile {
  file: File;
  handle?: StoredFileHandle;
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  const db = await openDb();
  if (!db) {
    return null;
  }

  return new Promise<T | null>((resolve) => {
    try {
      const transaction = db.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(null);
      transaction.oncomplete = () => db.close();
      transaction.onerror = () => resolve(null);
      transaction.onabort = () => resolve(null);
    } catch {
      // Firefox and Safari cannot structured-clone a handle, so the put itself throws.
      db.close();
      resolve(null);
    }
  });
}

/** Files a `<input type="file">` produced carry no handle; that is the fallback path, not an error. */
export async function rememberFileHandle(audioId: string, handle?: StoredFileHandle): Promise<void> {
  if (!handle) {
    return;
  }
  await withStore('readwrite', store => store.put(handle, audioId));
}

export async function forgetFileHandle(audioId: string): Promise<void> {
  await withStore('readwrite', store => store.delete(audioId));
}

/**
 * The remembered file, or `null` when there is nothing stored, the browser cannot store one, or the
 * user declines to re-grant access.
 *
 * **Must be called inside the click handler.** `requestPermission` shows a prompt, and a browser
 * only allows that while it can still see the user's gesture — awaiting anything slow first turns
 * a silent re-grant into a silent refusal.
 */
export async function fileFromRememberedHandle(audioId: string): Promise<File | null> {
  const handle = await withStore<StoredFileHandle>('readonly', store => store.get(audioId));

  if (!handle || typeof handle.getFile !== 'function') {
    return null;
  }

  try {
    const descriptor = {mode: 'read'} as const;
    const existing = await handle.queryPermission?.(descriptor);

    if (existing !== 'granted') {
      const granted = await handle.requestPermission?.(descriptor);
      if (granted !== 'granted') {
        return null;
      }
    }

    return await handle.getFile();
  } catch {
    // The file was moved, renamed or deleted since it was picked. Falling back to the picker is
    // exactly right — the user is about to tell us where it is.
    return null;
  }
}

/**
 * The picker that hands back handles, when the browser has one.
 *
 * Returns `null` when the API does not exist so the caller can fall back to its hidden
 * `<input type="file">`, and an empty array when the user cancelled — two different things.
 */
export async function pickAudioFilesWithHandles(
  accept: Record<string, string[]>,
  multiple: boolean,
): Promise<PickedAudioFile[] | null> {
  const picker = (window as FilePickerWindow).showOpenFilePicker;

  if (typeof picker !== 'function') {
    return null;
  }

  try {
    const handles = await picker.call(window, {
      multiple,
      types: [{description: 'Audio', accept}],
    });

    return await Promise.all(handles.map(async handle => ({file: await handle.getFile(), handle})));
  } catch (error) {
    // `AbortError` is the user dismissing the picker; anything else means the API is unusable here
    // (a sandboxed frame, say), and the input fallback covers it.
    if (error instanceof DOMException && error.name === 'AbortError') {
      return [];
    }
    return null;
  }
}

/**
 * Pulls handles off a drop when the browser exposes them, falling back to plain `File`s.
 *
 * **Everything is read off the `DataTransfer` synchronously.** The item list is neutered the moment
 * the drop event finishes dispatching, so the handle promise and the `File` are both taken up front
 * and only then awaited — reaching back for `getAsFile()` after an `await` returns null.
 */
export async function pickedFilesFromDrop(dataTransfer: DataTransfer): Promise<PickedAudioFile[]> {
  const items = Array.from(dataTransfer.items) as HandleCapableDataTransferItem[];

  const claimed = items
    .filter(item => item.kind === 'file')
    .map((item) => {
      let handlePromise: Promise<StoredFileHandle | null> | null = null;
      try {
        handlePromise = item.getAsFileSystemHandle?.() ?? null;
      } catch {
        handlePromise = null;
      }
      return {file: item.getAsFile(), handlePromise};
    });

  // No items at all (some browsers only populate `files`), so take the plain list.
  if (claimed.length === 0) {
    return Array.from(dataTransfer.files).map(file => ({file}));
  }

  const picked = await Promise.all(claimed.map(async ({file, handlePromise}) => {
    const handle = await handlePromise?.catch(() => null);

    if (handle && typeof handle.getFile === 'function') {
      try {
        return {file: await handle.getFile(), handle};
      } catch {
        // Fall through to the File the drop already gave us.
      }
    }

    return file ? {file} : null;
  }));

  return picked.filter((entry): entry is PickedAudioFile => entry !== null);
}
